/**
 * /settings/permission-groups (v7 — DESIGN-V7 §1, fiel à captura
 * tiflux-50-grupos-permissao.png): breadcrumb "Configurações / Usuários /
 * Grupos de permissões", busca à direita + botão "+ Grupo de permissão",
 * tabela de uma coluna com o nome em link (→ detalhe) e lixeira por linha.
 * Criar = dialog nome+descrição → POST → navega ao detalhe recém-criado.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import type { PermissionGroup, PermissionGroupBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { normalizeText, SettingsBreadcrumb, SettingsSearch } from '@/features/settings/settings-ui';
import { errorMessage, permissionGroupsListOptions } from '@/features/permissions/permissions-queries';

export function PermissionGroupsPage() {
  const queryClient = useQueryClient();
  const groupsQ = useQuery(permissionGroupsListOptions());
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const remove = useMutation({
    mutationFn: async (groupId: number) => {
      const res = await api<void>(`/permission-groups/${groupId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['erp', 'permission-groups'] }),
  });

  const q = normalizeText(search.trim());
  const groups = groupsQ.data ?? [];
  const filtered = q ? groups.filter((g) => normalizeText(g.name).includes(q)) : groups;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <SettingsBreadcrumb section="Usuários" page="Grupos de permissões" />
          <h1 className="mt-2 text-xl leading-7 font-semibold">Grupos de permissões</h1>
        </div>
        <div className="flex items-center gap-2">
          <SettingsSearch value={search} onChange={setSearch} label="Buscar grupo de permissão" />
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-4" strokeWidth={2.5} />
            Grupo de permissão
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
        {groupsQ.isPending ? (
          <div className="space-y-2 p-4" aria-busy>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : groupsQ.isError ? (
          <EmptyState message={`Erro ao carregar grupos: ${errorMessage(groupsQ.error)}`} />
        ) : filtered.length === 0 ? (
          <EmptyState
            message={
              q ? `Nenhum grupo de permissão para “${search.trim()}”` : 'Não há dados'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-9 px-4 font-semibold">Grupos de permissões</th>
                  <th className="h-9 w-16 px-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((g) => (
                  <tr
                    key={g.id}
                    className="border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-96 truncate px-4 py-2.5">
                      <Link
                        to="/settings/permission-groups/$groupId"
                        params={{ groupId: String(g.id) }}
                        className="font-medium text-primary transition-colors hover:text-primary-pressed"
                      >
                        {g.name}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          className="text-muted-foreground hover:text-destructive"
                          title="Excluir grupo de permissão"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`Excluir o grupo de permissão "${g.name}"?`))
                              remove.mutate(g.id);
                          }}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {remove.error ? (
          <p className="px-4 py-3 text-sm text-destructive">{errorMessage(remove.error)}</p>
        ) : null}
      </div>

      {creating ? <CreatePermissionGroupDialog onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

/** Nome + descrição → POST /permission-groups → navega ao detalhe. */
function CreatePermissionGroupDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const create = useMutation({
    mutationFn: async (body: PermissionGroupBody) => {
      const res = await api<PermissionGroup>('/permission-groups', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (group) => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'permission-groups'] });
      onClose();
      void navigate({
        to: '/settings/permission-groups/$groupId',
        params: { groupId: String(group.id) },
      });
    },
  });

  const valid = name.trim().length > 0;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    create.mutate({
      name: name.trim(),
      description: description.trim() ? description.trim() : null,
      permissions: [],
      userIds: [],
      attendantGroupIds: [],
      deskIds: null,
    });
  };

  return (
    <Dialog open onClose={onClose} title="Novo grupo de permissão">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="permission-group-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="permission-group-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="permission-group-description">Descrição</Label>
          <Textarea
            id="permission-group-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
        {create.error ? (
          <p className="text-sm text-destructive">{errorMessage(create.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={create.isPending || !valid}>
            {create.isPending ? 'Criando…' : 'Criar grupo'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
